import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "fp_cookie_consent_v1";

const DEFAULT_PREFS = {
  necessary: true,
  analytics: false,
  ads: false,
  personalization: false,
};

function applyConsent(prefs) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  window.gtag("consent", "update", {
    ad_storage: prefs.ads ? "granted" : "denied",
    ad_user_data: prefs.ads ? "granted" : "denied",
    ad_personalization: prefs.ads ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    personalization_storage: prefs.personalization ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
  window.dataLayer.push({ event: "cookie_consent_update", prefs });
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPrefs({ ...DEFAULT_PREFS, ...parsed });
        applyConsent({ ...DEFAULT_PREFS, ...parsed });
      } else {
        setVisible(true);
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const openPrefsHandler = useCallback(() => {
    setVisible(true);
    setManage(true);
  }, []);

  useEffect(() => {
    window.addEventListener("fp:open-cookie-prefs", openPrefsHandler);
    return () => window.removeEventListener("fp:open-cookie-prefs", openPrefsHandler);
  }, [openPrefsHandler]);

  const persist = (next) => {
    setPrefs(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {}
    applyConsent(next);
    setVisible(false);
    setManage(false);
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, ads: true, personalization: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, ads: false, personalization: false });
  const save = () => persist(prefs);

  if (!visible) return null;

  return (
    <div className="fp-cookie" role="dialog" aria-live="polite" aria-label={t("cookie_banner.title")}>
      <div className="fp-cookie__inner">
        <div className="fp-cookie__intro">
          <div className="fp-cookie__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M12 2a10 10 0 1 0 10 10 6 6 0 0 1-6-6 4 4 0 0 1-4-4z" />
              <circle cx="8" cy="12" r="1" />
              <circle cx="13" cy="9" r="1" />
              <circle cx="15" cy="15" r="1" />
              <circle cx="10" cy="17" r="1" />
            </svg>
          </div>
          <div className="fp-cookie__text">
            <h3>{t("cookie_banner.title")}</h3>
            <p>{t("cookie_banner.body")}</p>
          </div>
        </div>

        {manage && (
          <div className="fp-cookie__prefs">
            <div className="fp-cookie__row">
              <div>
                <strong>{t("cookie_banner.categories.necessary.title")}</strong>
                <span>{t("cookie_banner.categories.necessary.body")}</span>
              </div>
              <span className="fp-cookie__req">{t("cookie_banner.categories.necessary.required")}</span>
            </div>
            {["analytics", "ads", "personalization"].map((k) => (
              <label key={k} className="fp-cookie__row fp-cookie__row--switch">
                <div>
                  <strong>{t(`cookie_banner.categories.${k}.title`)}</strong>
                  <span>{t(`cookie_banner.categories.${k}.body`)}</span>
                </div>
                <span className="fp-switch">
                  <input
                    type="checkbox"
                    checked={!!prefs[k]}
                    onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })}
                  />
                  <span className="fp-switch__track" />
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="fp-cookie__actions">
          {!manage ? (
            <>
              <button type="button" className="fp-btn fp-btn--ghost" onClick={() => setManage(true)}>
                {t("cookie_banner.manage")}
              </button>
              <button type="button" className="fp-btn fp-btn--outline" onClick={rejectAll}>
                {t("cookie_banner.reject")}
              </button>
              <button type="button" className="fp-btn fp-btn--primary" onClick={acceptAll}>
                {t("cookie_banner.accept")}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="fp-btn fp-btn--ghost" onClick={rejectAll}>
                {t("cookie_banner.reject")}
              </button>
              <button type="button" className="fp-btn fp-btn--outline" onClick={acceptAll}>
                {t("cookie_banner.accept")}
              </button>
              <button type="button" className="fp-btn fp-btn--primary" onClick={save}>
                {t("cookie_banner.save")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
